import { Component, OnDestroy, OnInit } from "@angular/core";
// import { HttpClient } from "@angular/common/http";
import { GloControlComponent } from "../../../shared/components/glo-control/glo-control.component";
// import { API_ENDPOINTS } from "../../../shared/services/api/api-endpoints";
import { NavigationEnd, Router, RouterLink } from "@angular/router";

import { filter, Subscription } from "rxjs";

import { NgSelectModule } from "@ng-select/ng-select";

import { AuthService } from "../../../shared/services/auth.service";

import { MatIconModule } from "@angular/material/icon";

import { environment } from "../../../../environments/environment";

import { GoogleMapsLoaderService } from "../../../shared/services/google-map-loader.service";

@Component({
  selector: "landing-header",

  standalone: true,

  imports: [GloControlComponent, RouterLink, NgSelectModule, MatIconModule],

  templateUrl: "./header.component.html",

  styleUrl: "./header.component.scss",
})
export class LandingHeaderComponent implements OnInit, OnDestroy {
  isLoggedIn: boolean = false;

  isProfileDrdnOpen: boolean = false;

  isBusiness = false;

  isPlatformAdmin = false;
  isBusinessOwner = false;
  isCustomer = false;
  isStaff = false;

  userRole = "";

  showMenu = true;

  assetUrl = environment.assetBaseUrl;

  user: any = null;

  /* =========================================================
     Location
  ========================================================== */

  /*
   * Keep your ORIGINAL glo-control option structure.
   *
   * These are only used when browser location is denied,
   * unavailable, or cannot be resolved.
   */
  cities = [
    { id: 1, name: "Toronto" },

    { id: 2, name: "Montreal" },

    { id: 3, name: "Vancouver" },

    { id: 4, name: "Calgary" },

    { id: 5, name: "Ottawa" },

    { id: 6, name: "Edmonton" },

    { id: 7, name: "Winnipeg" },

    { id: 8, name: "Halifax" },

    { id: 9, name: "Quebec City" },

    { id: 10, name: "Hamilton" },

    { id: 11, name: "Victoria" },
  ];

  selectedCityId = 1;

  /*
   * Successful geolocation is NOT restricted
   * to the cities array.
   *
   * This can be any city in the world.
   */
  detectedCityName: string | null = null;

  detectingLocation = false;

  detectedLatitude: number | null = null;

  detectedLongitude: number | null = null;

  private subscription: Subscription;

  constructor(
    private readonly router: Router,
    private readonly authService: AuthService,
    private readonly googleMapsLoader: GoogleMapsLoaderService,
    // private readonly http: HttpClient,
  ) {
    this.subscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        const basePath = this.router.url.split("?")[0].split("/")[1];

        this.isBusiness = basePath === "business";
      });
  }

  /* =========================================================
     Init
  ========================================================== */

  ngOnInit(): void {
    this.setMenuVisibility();

    this.loadUser();

    this.detectCurrentCity();
  }

  /* =========================================================
     User
  ========================================================== */

  private loadUser(): void {
    const token = this.authService.getToken();

    if (!token) {
      this.user = null;
      this.isLoggedIn = false;
      this.isPlatformAdmin = false;
      this.isBusinessOwner = false;
      this.isCustomer = false;
      this.isStaff = false;
      this.userRole = "";
      return;
    }

    this.isLoggedIn = true;

    this.authService.getUserDetails().subscribe({
      next: (response: any) => {
        const user =
          response?.data?.user ?? response?.data ?? response?.user ?? response;

        this.user = user;
        this.isLoggedIn = true;

        const role = String(user?.role_name ?? "")
          .trim()
          .toLowerCase();

        this.userRole = role;

        // Platform administrator privileges come from Django.
        this.isPlatformAdmin = user?.is_platform_admin === true;

        // Application roles are separate from platform administration.
        this.isBusinessOwner = role === "business_owner";
        this.isCustomer = role === "customer";
        this.isStaff = role === "staff";
      },

      error: (error) => {
        console.error("Unable to load user details:", error);

        if (error.status === 401) {
          this.user = null;
          this.isLoggedIn = false;
          this.isPlatformAdmin = false;
          this.isBusinessOwner = false;
          this.isCustomer = false;
          this.isStaff = false;
          this.userRole = "";
        }
      },
    });
  }

  onFallbackCityChange(value: any): void {
    const selectedId = Number(value);

    this.selectedCityId = selectedId;

    const selectedCity = this.cities.find((city) => city.id === selectedId);

    if (!selectedCity) {
      return;
    }

    if (typeof localStorage !== "undefined") {
      localStorage.setItem("gloget_city", selectedCity.name);

      localStorage.setItem("gloget_location_source", "manual");

      // Remove old coordinates because they may belong
      // to a previously detected city.
      localStorage.removeItem("gloget_latitude");
      localStorage.removeItem("gloget_longitude");
    }
  }

  /* =========================================================
     Browser location
  ========================================================== */

  private detectCurrentCity(): void {
    /*
     * Start with the loading state.
     *
     * If anything fails, the HTML will fall back
     * to your existing glo-control.
     */
    this.detectingLocation = true;

    this.detectedCityName = null;

    this.selectedCityId = 1;

    if (
      typeof window === "undefined" ||
      typeof navigator === "undefined" ||
      !navigator.geolocation
    ) {
      this.showLocationFallback();

      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const latitude = position.coords.latitude;

        const longitude = position.coords.longitude;

        this.detectedLatitude = latitude;

        this.detectedLongitude = longitude;

        this.reverseGeocodeCity(latitude, longitude);
      },

      (error) => {
        console.info("Unable to detect current location:", error.message);

        this.showLocationFallback();
      },

      {
        enableHighAccuracy: false,

        timeout: 10000,

        maximumAge: 10 * 60 * 1000,
      },
    );
  }

  /* =========================================================
     Reverse geocode using your existing loader
  ========================================================== */

  private async reverseGeocodeCity(
    latitude: number,

    longitude: number,
  ): Promise<void> {
    try {
      /*
       * THIS IS THE IMPORTANT FIX.
       *
       * Your GoogleMapComponent already does this:
       *
       * await this.loader.load();
       *
       * so the header must do the same thing before
       * accessing google.maps.Geocoder.
       */
      await this.googleMapsLoader.load();

      const googleMaps = (window as any)?.google?.maps;

      if (!googleMaps?.Geocoder) {
        console.error("Google Maps loaded but Geocoder is unavailable.");

        this.showLocationFallback();

        return;
      }

      const geocoder = new googleMaps.Geocoder();

      geocoder.geocode(
        {
          location: {
            lat: latitude,

            lng: longitude,
          },
          language: "en",
        },

        (
          results: any[] | null,

          status: string,
        ) => {
          if (status !== "OK" || !results?.length) {
            console.info("Unable to reverse geocode location:", status);

            this.showLocationFallback();

            return;
          }

          const cityName = this.extractCityName(results);

          if (!cityName) {
            console.info(
              "Google Maps returned an address but no city could be determined.",
            );

            this.showLocationFallback();

            return;
          }

          /*
           * SUCCESS.
           *
           * No comparison against this.cities.
           *
           * London, Miami, Tokyo, Mississauga, etc.
           * are all perfectly valid.
           */
          this.detectedCityName = cityName;

          this.detectingLocation = false;

          this.saveDetectedLocation(
            cityName,

            latitude,

            longitude,
          );
        },
      );
    } catch (error) {
      console.error(
        "Unable to load Google Maps for location detection:",
        error,
      );

      this.showLocationFallback();
    }
  }

  /* =========================================================
     Extract worldwide city
  ========================================================== */

  private extractCityName(results: any[]): string | null {
    /*
     * Google address structures vary around
     * the world.
     *
     * locality is normally what we want.
     *
     * postal_town matters particularly in
     * some countries such as the UK.
     */
    const preferredTypes = [
      "locality",

      "postal_town",

      "administrative_area_level_3",

      "administrative_area_level_2",

      "administrative_area_level_1",
    ];

    for (const type of preferredTypes) {
      for (const result of results) {
        const components = result?.address_components ?? [];

        const component = components.find(
          (item: any) => Array.isArray(item.types) && item.types.includes(type),
        );

        const name = component?.long_name?.trim();

        if (name) {
          return name;
        }
      }
    }

    return null;
  }

  /* =========================================================
     Fallback
  ========================================================== */

  private showLocationFallback(): void {
    this.detectingLocation = false;

    /*
     * null causes the HTML to show the
     * normal glo-control.
     */
    this.detectedCityName = null;

    /*
     * Toronto remains your default.
     */
    this.selectedCityId = 1;
  }

  /* =========================================================
     Save location
  ========================================================== */

  private saveDetectedLocation(
    cityName: string,

    latitude: number,

    longitude: number,
  ): void {
    if (typeof localStorage === "undefined") {
      return;
    }

    localStorage.setItem("gloget_city", cityName);

    localStorage.setItem("gloget_latitude", String(latitude));

    localStorage.setItem("gloget_longitude", String(longitude));

    localStorage.setItem("gloget_location_source", "detected");
  }

  /* =========================================================
     Existing behavior
  ========================================================== */

  setMenuVisibility(): void {
    if (typeof window !== "undefined") {
      this.showMenu = window.innerWidth > 768;
    }
  }

  signOut(): void {
    localStorage.removeItem("access_token");

    this.isLoggedIn = false;
    this.user = null;
    this.isProfileDrdnOpen = false;

    this.isPlatformAdmin = false;
    this.isBusinessOwner = false;
    this.isCustomer = false;
    this.isStaff = false;
    this.userRole = "";

    this.router.navigateByUrl("/login");
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
